import React from 'react';
import { Participant, Track } from 'livekit-client';
import { Avatar } from 'features/Avatar/Avatar';
import { BlurredSnapshot } from '../components/BlurredSnapshot';
import { AvatarOverlay, BasePlaceholderProps } from '../presenters/view';

interface ParticipantVideoContentProps {
    track: Track | undefined;
    isLocal: boolean;
    isShowVideo: boolean;
    isVideoReady: boolean;
    isVideoLoading: boolean;
    snapshot: string | null;
    shouldShowSnapshotInsteadOfAvatar: boolean;
    objectFit: string;
    participant: Participant;
    placeholder: string;
    withSpeakerPulseAnimation?: boolean;
    onSizeChanged?: (width: number, height: number) => void;
    VideoRenderer: React.ComponentType<{
        track: Track;
        isLocal: boolean;
        $objectFit: string;
        $isReady: boolean;
        participantId: string;
        onSizeChanged?: (width: number, height: number) => void;
    }>;
    Placeholder: React.ComponentType<BasePlaceholderProps>;
    snapshotFirst?: boolean;
}

/**
 * Важно: VideoRenderer не должен размонтироваться при появлении/скрытии snapshot.
 * Иначе attach/detach и isVideoReady мигают → вечный blur без live-видео.
 */
export const ParticipantVideoContent = ({
    track,
    isLocal,
    withSpeakerPulseAnimation,
    isShowVideo,
    isVideoReady,
    isVideoLoading,
    snapshot,
    shouldShowSnapshotInsteadOfAvatar,
    objectFit,
    participant,
    placeholder,
    onSizeChanged,
    VideoRenderer,
    Placeholder,
    snapshotFirst = false,
}: ParticipantVideoContentProps) => {
    // Вне скролла / без live-трека: оставляем snapshot, если камера ещё on
    if (!isShowVideo || !track) {
        if (shouldShowSnapshotInsteadOfAvatar && snapshot) {
            return <BlurredSnapshot snapshotUrl={snapshot} isVideoReady={false} />;
        }

        return (
            <Avatar
                participant={participant}
                PresenterPlaceholder={Placeholder}
                isShowPulseAnimation={withSpeakerPulseAnimation}
            >
                {placeholder}
            </Avatar>
        );
    }

    const videoElement = (
        <VideoRenderer
            track={track}
            isLocal={isLocal}
            $objectFit={objectFit}
            $isReady={isVideoReady}
            participantId={participant.identity}
            onSizeChanged={onSizeChanged}
        />
    );

    const snapshotElement = shouldShowSnapshotInsteadOfAvatar ? (
        <BlurredSnapshot snapshotUrl={snapshot} isVideoReady={isVideoReady} />
    ) : null;

    const avatarLoader =
        !snapshot && isVideoLoading ? (
            <AvatarOverlay>
                <Avatar
                    participant={participant}
                    PresenterPlaceholder={Placeholder}
                    isShowPulseAnimation={withSpeakerPulseAnimation}
                >
                    {placeholder}
                </Avatar>
            </AvatarOverlay>
        ) : null;

    if (snapshotFirst) {
        return (
            <>
                {videoElement}
                {snapshotElement}
                {avatarLoader}
            </>
        );
    }

    return (
        <>
            {snapshotElement}
            {videoElement}
            {avatarLoader}
        </>
    );
};
